import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Paperclip, FileText } from 'lucide-react';
import { Expense } from '../../types';
import { useNavigate } from 'react-router-dom';

interface RecentActivityProps {
  expenses: Expense[];
  maxItems?: number;
}

const RecentActivity: React.FC<RecentActivityProps> = ({
  expenses,
  maxItems = 10,
}) => {
  const navigate = useNavigate();

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Hoy';
    } else if (diffDays === 1) {
      return 'Ayer';
    } else if (diffDays < 7) {
      return `Hace ${diffDays} días`;
    } else {
      return date.toLocaleDateString('es-CL', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    }
  };

  const handleExpenseClick = () => {
    navigate('/expenses');
  };

  const displayedExpenses = expenses.slice(0, maxItems);

  if (expenses.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Actividad Reciente</CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8">
          <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">
            No hay gastos registrados aún
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Actividad Reciente</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/expenses')}
          >
            Ver todos
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="divide-y">
          {displayedExpenses.map((expense) => (
            <div
              key={expense.expenseId}
              onClick={() => handleExpenseClick()}
              className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-sm font-medium truncate">
                      {expense.provider}
                    </p>
                    {expense.attachmentIds && expense.attachmentIds.length > 0 && (
                      <Paperclip className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {expense.projectName && (
                      <Badge variant="secondary" className="text-xs">
                        {expense.projectName}
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-xs">
                      {expense.categoryName}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {expense.subcategoryName}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-1">
                    {expense.description}
                  </p>
                </div>
                <div className="ml-4 flex-shrink-0 text-right">
                  <p className="text-sm font-bold">
                    {formatCurrency(expense.amount)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDate(expense.date)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {expenses.length > maxItems && (
          <div className="p-4 bg-muted/50 text-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/expenses')}
            >
              Ver {expenses.length - maxItems} gastos más
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentActivity;
